// Groomba Content Kit
// Page-level surfaces for documentation, changelog, blog, etc.
// Depends on React + content.css + the parent Groomba design tokens (--gr-*).

import React, { Fragment } from 'react';
import { SkCompose } from '../slack/Slack';

/* ──────────────────────────────────────────────
   Tiny inline icon set
   ────────────────────────────────────────────── */
function DcIco({ name }) {
  const p = {
    info:    'M8 5.5v0M8 8v3.5',
    check:   'M3 8l3 3 6-6',
    warn:    'M8 4v4M8 11v0',
    chevron: 'M5 3l4 4-4 4',
    arrow:   'M3 8h10M9 4l4 4-4 4',
    chevUp:  'm18 15-6-6-6 6',
  };
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={p[name]} />
    </svg>
  );
}

/* ──────────────────────────────────────────────
   Prose wrapper
   ────────────────────────────────────────────── */
export function Prose({ children, className }) {
  return <div className={['gr-prose', className].filter(Boolean).join(' ')}>{children}</div>;
}

export function ProseHeading({ level = 2, id, children }) {
  const Tag = `h${level}`;
  return (
    <Tag id={id}>
      {children}
      {id && <a className="anchor" href={`#${id}`} aria-label="Permalink"> #</a>}
    </Tag>
  );
}

/* ──────────────────────────────────────────────
   Callout (Note / Tip / Warning / Important)
   ────────────────────────────────────────────── */
export function Callout({ kind = 'note', title, children }) {
  const icon = {
    note:   <DcIco name="info" />,
    tip:    <DcIco name="check" />,
    warn:   <DcIco name="warn" />,
    danger: <DcIco name="warn" />,
  }[kind];
  const label = { note: 'Note', tip: 'Tip', warn: 'Warning', danger: 'Important' }[kind];
  return (
    <aside className={`gr-callout ${kind}`}>
      <span className="ico" aria-hidden="true">{icon}</span>
      <div className="body">
        <strong>{title || label}</strong>
        {children}
      </div>
    </aside>
  );
}

/* ──────────────────────────────────────────────
   DocsNavPath — "type /command → click button → click button"
   The terminal step pulses to read as "click here". When the
   slash command itself is the only action (no buttons), the
   pulse moves onto the green send button instead.
   ────────────────────────────────────────────── */
export function DocsNavPath({ command, steps = [], value }) {
  const lastIdx = steps.length - 1;
  const composeIsTick = !!command && steps.length === 0 && !value;
  return (
    <div className="docs-navpath" role="group" aria-label="Navigation path">
      {command && (
        <span className={`docs-navpath-step is-compose slack-root ${composeIsTick ? 'is-tick' : ''}`}>
          <SkCompose command={command} showCaret={false} />
          {composeIsTick && <Cursor />}
        </span>
      )}
      {steps.map((label, i) => (
        <Fragment key={i}>
          <span className="docs-navpath-arrow" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className={`docs-navpath-step is-button ${i === lastIdx && !value ? 'is-tick' : ''}`}>
            <span className="docs-navpath-button">{label}</span>
            {i === lastIdx && !value && <Cursor />}
          </span>
        </Fragment>
      ))}
      {value && (
        <>
          <span className="docs-navpath-arrow" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className="docs-navpath-step is-value is-tick">
            <span className="docs-navpath-value">{value}</span>
            <Cursor />
          </span>
        </>
      )}
    </div>
  );
}

function Cursor() {
  return (
    <span className="docs-navpath-cursor" aria-hidden="true">
      <svg width="18" height="20" viewBox="0 0 18 20" fill="none">
        <path d="M2 2l4.5 14 2.5-5 5.5-1z" fill="#1D1C1D" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

/* ──────────────────────────────────────────────
   DefList — term/description list with first-class title.
     <DefList title="Possible poll results">
       <DefItem term="Estimate">An averaged point value…</DefItem>
     </DefList>
   ────────────────────────────────────────────── */
export function DefList({ title, children }) {
  return (
    <section className="gr-deflist">
      {title && <h5 className="gr-deflist-title">{title}</h5>}
      <dl>{children}</dl>
    </section>
  );
}

export function DefItem({ term, children }) {
  return (
    <>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </>
  );
}

/* ──────────────────────────────────────────────
   FieldOptionList — selectable spec rows (e.g. estimate-field options)
   ────────────────────────────────────────────── */
export function FieldOptionList({ legend, children }) {
  return (
    <div className="gr-fieldopts" role="group" aria-label={legend}>
      {legend ? <div className="gr-fieldopts-legend">{legend}</div> : null}
      <ul className="gr-fieldopts-list">{children}</ul>
    </div>
  );
}

export function FieldOption({ name, badge, recommended, children }) {
  return (
    <li className={`gr-fieldopt${recommended ? ' is-recommended' : ''}`}>
      <div className="gr-fieldopt-body">
        <div className="gr-fieldopt-head">
          <span className="gr-fieldopt-name">{name}</span>
          {badge && <span className="gr-fieldopt-badge">{badge}</span>}
          {recommended && <span className="gr-fieldopt-rec">Recommended</span>}
        </div>
        <div className="gr-fieldopt-desc">{children}</div>
      </div>
    </li>
  );
}

/* ──────────────────────────────────────────────
   DocsFooter — quiet end-of-page footer for single-doc layouts.
   ────────────────────────────────────────────── */
export function DocsFooter({ updated, version }) {
  return (
    <footer className="docs-footer">
      <div className="docs-footer-col">
        <span className="docs-footer-label">Need more help?</span>
        <ul className="docs-footer-links">
          <li>
            <a href="#feedback">
              Submit feedback
              <span className="docs-footer-link-aside">via <code>/groomba feedback</code></span>
            </a>
          </li>
          <li><a href="mailto:hello@groomba.ai">Contact support</a></li>
          <li><a href="/changelog">Read the changelog</a></li>
        </ul>
      </div>
      <div className="docs-footer-col docs-footer-meta">
        <span className="docs-footer-label">This page</span>
        <div>
          {updated && <div>Last updated <strong>{updated}</strong></div>}
          {version && <div>Groomba <strong>{version}</strong></div>}
        </div>
        <a href="#top" className="docs-footer-toplink" aria-label="Back to top">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m18 15-6-6-6 6"/>
          </svg>
          Back to top
        </a>
      </div>
    </footer>
  );
}
